import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

/**
 * Replaces `[key]` placeholders in a merchant-editable text.
 * @param {string | undefined} template - The text with placeholders.
 * @param {Record<string, string | number>} values - The values to fill in.
 * @returns {string} The filled text.
 */
function fillTemplate(template, values) {
  return (template ?? '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));
}

/**
 * Sets the text of an element only when it changed, so live regions aren't re-announced.
 * @param {Element | null | undefined} element - The element to update.
 * @param {string} text - The new text.
 */
function setText(element, text) {
  if (element && element.textContent?.trim() !== text) element.textContent = text;
}

/**
 * @typedef {object} SetLine
 * @property {HTMLElement} element - The item element.
 * @property {HTMLInputElement | null} toggle - The checkbox that includes the piece, none for the current product.
 * @property {boolean} included - Whether the piece is part of the set.
 * @property {string} variantId - The chosen variant ID, empty until one is chosen.
 * @property {number} price - The price in minor units.
 */

/**
 * A custom element that lets shoppers add the current product and its matching products as one set.
 * The offer tier, the totals and the button follow the pieces that are ticked.
 *
 * @typedef {object} SetOfferRefs
 * @property {HTMLFormElement} form - The set form.
 * @property {HTMLElement} thisItem - The item for the current product.
 * @property {HTMLElement[]} [items] - The items for the matching products.
 * @property {HTMLButtonElement[]} [tiers] - The offer tier buttons.
 * @property {HTMLElement} [summaryText] - The summary text.
 * @property {HTMLElement} [compareTotal] - The total before the offer.
 * @property {HTMLElement} [total] - The total after the offer.
 * @property {HTMLElement} [note] - The note shown under the totals.
 * @property {HTMLElement} [noteText] - The note text.
 * @property {HTMLElement} [error] - The error message.
 *
 * @extends {Component<SetOfferRefs>}
 */
class SetOfferComponent extends Component {
  requiredRefs = ['form', 'thisItem'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #errorTimeout;

  /** @type {boolean} */
  #isAdding = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    this.update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
  }

  updatedCallback() {
    super.updatedCallback();
    this.update();
  }

  /** @returns {HTMLButtonElement | null} */
  get #addToCartButton() {
    return this.querySelector('[ref="addToCartButton"]');
  }

  /** @returns {number[]} The discount of each tier, in percent. */
  get #tiers() {
    return (this.dataset.tiers ?? '').split(',').map(Number);
  }

  /**
   * Reads the pieces of the set from the DOM.
   * @returns {SetLine[]} The current product first, then the matching products.
   */
  #getLines() {
    const { thisItem, items = [] } = this.refs;

    /** @type {SetLine[]} */
    const lines = [
      {
        element: thisItem,
        toggle: null,
        included: true,
        variantId: thisItem.dataset.variantId ?? '',
        price: Number(thisItem.dataset.price) || 0,
      },
    ];

    for (const element of items) {
      const toggle = element.querySelector('input[type="checkbox"]');
      const select = element.querySelector('select');
      let variantId = element.dataset.variantId ?? '';
      let price = Number(element.dataset.price) || 0;

      if (select) {
        const option = select.selectedOptions[0];
        variantId = select.value;
        if (option?.dataset.price) price = Number(option.dataset.price);
      }

      lines.push({
        element,
        toggle: toggle instanceof HTMLInputElement ? toggle : null,
        included: toggle instanceof HTMLInputElement ? toggle.checked : true,
        variantId,
        price,
      });
    }

    return lines;
  }

  /**
   * @param {number} value - The money value in minor units.
   * @returns {string} The formatted money value.
   */
  #formatMoney(value) {
    return formatMoney(value, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? '');
  }

  /**
   * Ticks as many pieces as the chosen tier needs.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event of a tier button.
   */
  selectTier(event) {
    const wanted = Number(event.target.dataset.count);
    if (!wanted) return;

    const extras = this.#getLines().slice(1);
    const count = 1 + extras.filter((line) => line.included).length;

    // Keep the shopper's own choice of pieces when it already matches the tier.
    if (count !== Math.min(wanted, extras.length + 1)) {
      extras.forEach((line, index) => {
        if (line.toggle) line.toggle.checked = index < wanted - 1;
      });
    }

    this.update();
  }

  /**
   * Syncs the tiers, the totals and the button with the pieces that are ticked.
   */
  update() {
    const lines = this.#getLines();
    const included = lines.filter((line) => line.included);
    const count = included.length;
    const tiers = this.#tiers;
    const activeTier = Math.min(count, tiers.length);
    const percent = tiers[activeTier - 1] ?? 0;
    const subtotal = included.reduce((sum, line) => sum + line.price, 0);
    const total = Math.round((subtotal * (100 - percent)) / 100);

    for (const line of lines.slice(1)) {
      line.element.toggleAttribute('data-excluded', !line.included);
      setText(line.element.querySelector('.set-offer__price'), this.#formatMoney(line.price));

      const select = line.element.querySelector('select');
      if (select) select.disabled = !line.included;
    }

    for (const tier of this.refs.tiers ?? []) {
      tier.setAttribute('aria-pressed', String(Number(tier.dataset.count) === activeTier));
    }

    const { summaryText, compareTotal, total: totalElement, note: noteElement, noteText, thisItem } = this.refs;
    const summary = count === 1 ? this.dataset.summaryOne : this.dataset.summaryOther;

    // The nudge only makes sense when the set has enough pieces to reach the top tier.
    let note = '';
    if (count >= tiers.length) {
      note = this.dataset.reward ?? '';
    } else if (lines.length >= tiers.length) {
      note = fillTemplate(this.dataset.nudge, { remaining: tiers.length - count });
    }

    setText(summaryText, fillTemplate(summary, { count, percent }));
    setText(compareTotal, this.#formatMoney(subtotal));
    compareTotal?.toggleAttribute('hidden', percent === 0);
    setText(totalElement, this.#formatMoney(total));
    setText(noteText, note);
    noteElement?.toggleAttribute('hidden', note === '');

    const button = this.#addToCartButton;
    if (!button) return;

    const available = thisItem.dataset.available === 'true';
    let label = fillTemplate(this.dataset.buttonLabel, { count });

    if (!available) {
      label = this.dataset.soldOut ?? '';
    } else if (count === 1) {
      label = this.dataset.buttonSingle ?? '';
    }

    button.disabled = !available || count < 2;
    setText(button.querySelector('.add-to-cart-text__content > span > span'), label);
  }

  /**
   * Adds the ticked pieces to the cart in one request.
   * @param {SubmitEvent} event - The submit event.
   */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#isAdding || this.#addToCartButton?.disabled) return;

    const lines = this.#getLines().filter((line) => line.included);
    if (lines.length < 2) return;

    if (lines.some((line) => !line.variantId)) {
      this.refs.form.reportValidity();
      return;
    }

    this.#isAdding = true;
    this.#hideError();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({
          merchandiseId: line.variantId,
          quantity: 1,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    const payload = {
      items: lines.map((line) => ({
        id: Number(line.variantId),
        quantity: 1,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        const didError = Boolean(response.status);

        if (didError) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          this.#showError(response.description || response.message);
        }

        const cart = await this.#fetchCart();

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'set-offer-component',
            sourceId: this.id,
            itemCount: lines.length,
            productId: this.dataset.productId,
            sections: response.sections,
            didError,
          },
        });
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#isAdding = false;
      });
  }

  /**
   * Fetches the cart the same way the product form does.
   * @returns {Promise<any>} The cart as returned by the Ajax API.
   */
  async #fetchCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * @param {string} [message] - The error message.
   */
  #showError(message) {
    const { error } = this.refs;
    if (!error || !message) return;

    error.textContent = message;
    error.hidden = false;

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => this.#hideError(), ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #hideError() {
    const { error } = this.refs;
    if (!error) return;

    error.hidden = true;
    error.textContent = '';
  }

  /**
   * Keeps the current product's piece in sync with the variant picker.
   * @param {import('@shopify/events').ProductSelectEvent} event - The product select event.
   */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (!detail.newProduct && detail.productId && detail.productId !== this.dataset.productId) return;

        const { thisItem } = this.refs;
        const newThisItem = detail.html.querySelector(
          `set-offer-component[data-block-id="${this.dataset.blockId}"] [ref="thisItem"]`
        );
        if (!(newThisItem instanceof HTMLElement)) return;

        morph(thisItem, newThisItem);
        thisItem.dataset.variantId = newThisItem.dataset.variantId ?? '';
        thisItem.dataset.price = newThisItem.dataset.price ?? '0';
        thisItem.dataset.available = newThisItem.dataset.available ?? 'false';

        this.update();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[set-offer] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('set-offer-component')) {
  customElements.define('set-offer-component', SetOfferComponent);
}
